// apps/merchant-v3/src/components/topbar/BalanceChip.tsx
//
// Phase 6 Wave 12 Task 42 — TopBar wallet balance chip rendered for
// PREPAID + CREDIT mode merchants (conditional render). Reads
// the BillingSummary.wallet_balance field surfaced by Wave 10 Task 32
// + flashes briefly when the wsClient (Wave 12 Task 40) emits a
// `WALLET_BALANCE_CHANGED` push on the `merchant.${id}.wallet`
// channel. Click navigates to `/wallet`.
//
// Distinct from the legacy Wave 1 `components/BalanceChip.tsx` shim,
// which read the always-null `useWalletBalance()` stub. This is the
// production chip used by the new BillingWidget composition; the legacy
// flat file remains so older imports keep compiling.
//
// data-testid: `topbar-balance-chip` (per task spec).
//
// vg-binding: PLAN:task-42
// vg-binding: P6.D-77
// vg-binding: P6.D-114
// vg-binding: P6.D-245
// vg-binding: P6.D-246
// vg-binding: P6.D-247
// vg-binding: P6.D-248
// vg-binding: G-70
// vg-binding: G-71

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';

import { useAuth } from '@/lib/auth/AuthProvider';
import { useBillingSummary } from '@/hooks/useBillingSummary';
import { subscribe, type WSMessage } from '@/services/wsClient';
import { useUser } from '@/store/user';
import type { WalletWsMessage } from '@/types/wallet';
import { cn } from '@/lib/cn';

const FLASH_MS = 1200;

function formatBalance(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function BalanceChip(): JSX.Element {
  const { t } = useTranslation('common');
  const { user: meUser } = useAuth();
  const storeUser = useUser();
  const summary = useBillingSummary();
  const merchantId = meUser?.id ?? storeUser?.id ?? null;
  const userCurrency = storeUser?.displayCurrency ?? meUser?.displayCurrency;

  // Track the most recently observed balance so we can flash on change.
  const lastBalanceRef = useRef<number | null>(null);
  const [flash, setFlash] = useState<boolean>(false);

  // Flash when BillingSummary refetches a different balance value.
  useEffect(() => {
    if (!summary) return;
    const next = summary.wallet_balance;
    const prev = lastBalanceRef.current;
    lastBalanceRef.current = next;
    if (prev !== null && prev !== next) {
      setFlash(true);
      const t = window.setTimeout(() => setFlash(false), FLASH_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [summary?.wallet_balance, summary]);

  // Live WS subscription — independently flash whenever the BE pushes a
  // WALLET_BALANCE_CHANGED frame even before the BillingSummary refetch
  // settles. Useful when the merchant is staring at the chip while a
  // topup completes on another tab.
  useEffect(() => {
    if (!merchantId) return;
    const channel = `merchant.${merchantId}.wallet`;
    const unsubscribe = subscribe(channel, (msg: WSMessage) => {
      const evt = msg as WalletWsMessage;
      if (evt && evt.type === 'WALLET_BALANCE_CHANGED') {
        setFlash(true);
        window.setTimeout(() => setFlash(false), FLASH_MS);
      }
    });
    return () => {
      unsubscribe();
    };
  }, [merchantId]);

  const currency = summary?.currency ?? userCurrency ?? 'USD';
  const label = summary
    ? formatBalance(summary.wallet_balance, currency)
    : '—';

  return (
    <Link
      to="/wallet"
      data-testid="topbar-balance-chip"
      aria-label={t('topbar.walletBalanceAria', { label, defaultValue: 'Wallet balance {{label}}' })}
      className={cn(
        'inline-flex h-9 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        flash
          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
          : 'border-pw-border bg-pw-card text-pw-text-muted hover:bg-pw-surface',
      )}
    >
      <Wallet
        className={cn('h-4 w-4', flash ? 'text-emerald-600' : 'text-pw-text-muted')}
        aria-hidden
      />
      <span>{label}</span>
      <span className="text-[10px] uppercase text-pw-text-muted">{currency}</span>
    </Link>
  );
}

export default BalanceChip;
